/**
 * Relation write executor — internal. Turns the reconcile ops `planRecordWrite`
 * produces into bulk inserts/deletes, stamping every new row with the field's
 * `schema_key` + `schema_id`. Dispatches by the field's relationship. Consumers
 * never call this — they go through `records.update` / `records.create`.
 *
 * One mechanism serves all six owned-row relationships the taxonomy declares:
 * links, taggings, attachments, contacts, times, sources. Coverage is pinned
 * exhaustively in tests/services/relationCoverage.test.ts, so a seventh relation
 * table cannot join the taxonomy without failing there.
 */

import type { ResultOf, TypedDocumentNode } from "@graphql-typed-document-node/core";
import type { RowUpdate } from "../collections/records.js";
import { assertTimeValues } from "../collections/times.js";
import { type AssetView, assetView, inferAssetType } from "../fields/assets.js";
import { type AugerField, getFieldType, isReferenceDef } from "../fields/fields.js";
import { fetchAssetsWhere } from "../gql/assets.js";
import {
  deleteAttachmentsByIds,
  insertManyRecordAttachments,
} from "../gql/attachments.js";
import {
  addManyRecordContacts,
  deleteManyRecordContacts,
  updateManyRecordContacts,
} from "../gql/contacts.js";
import { deleteLinksByIds, insertManyRecordLinks } from "../gql/links.js";
import type {
  RecordAttachmentFieldsFragment,
  RecordContactFieldsFragment,
  RecordLinkFieldsFragment,
  RecordSourceFieldsFragment,
  RecordTaggingFieldsFragment,
  RecordTimeFieldsFragment,
} from "../gql/sharedTypes.js";
import { deleteSourcesByIds, insertManyRecordSources } from "../gql/sources.js";
import {
  deleteTaggingsByIds,
  insertManyRecordTaggings,
} from "../gql/taggings.js";
import {
  createManyRecordTimes,
  deleteManyRecordTimes,
  updateManyRecordTimes,
} from "../gql/times.js";
import type { Mutate, Query } from "../index.js";

type Row = Record<string, unknown>;
type Entity = { id: number | string; [k: string]: unknown };

/**
 * Optional columns each relationship's row accepts, and the camelCase target
 * property that feeds each (falling back to the snake_case column name, matching
 * `contactType ?? contact_type` below). Declared per relationship on purpose.
 *
 * Two things earn an entry. First, parity with the raw pre-cutover MCP tool for
 * THAT relationship — not parity with any one shipped writer: pre-cutover
 * `auger-mcp/src/tools/attachments.ts` wrote `label`/`is_primary`/`position`/
 * `context`, so attachments gets them; pre-cutover `auger-mcp/src/tools/links.ts`
 * wrote only `record_id`/`linked_id`/`schema_id`/`schema_key`, and the pre-cutover
 * tagging mutations (deleted in cee83f725) wrote only `record_id`/`tag_id` — so
 * links and taggings get no entries. Second, parity with another shipped door
 * onto the SAME table — see `sources` below.
 *
 * Which columns each door can and cannot reach, and the reason for every
 * omission, is pinned in tests/services/insertInputCoverage.test.ts.
 *
 * `record_links` and `record_taggings` are NOT missing these columns — migrations
 * show `record_taggings` has all four and `record_links` has `position` — they
 * are simply not written by any current caller through this path. That matters
 * because a link/tagging target often carries same-named properties of its own
 * (a link's target is a resolved record entity, which may well have its own
 * `label` or `position`); those properties mean something else and must not be
 * copied into the junction row just because the column happens to exist. Only a
 * column listed here for the relationship at hand is ever copied, and only when
 * the target actually provides it (`!== undefined`).
 */
const OPTIONAL_COLUMNS: Record<string, string[]> = {
  contacts: ["label", "isPrimary", "position", "data"],
  // `assetType` overrides the mime-derived default below for a caller that has
  // already classified the asset (the frontend infers it from the upload).
  attachments: ["assetType", "label", "isPrimary", "position", "context"],
  // `sources` earns its entry from the OTHER source door rather than from a
  // pre-cutover tool: `services/sources.ts:add` already accepts isPrimary and
  // position, so a caller setting them on a source FIELD had them silently
  // dropped while the same values through `sources.add` landed. Two doors onto
  // one table disagreeing about which columns exist is the defect this map
  // exists for. `label`/`context` are set by the sources branch itself.
  sources: ["isPrimary", "position"],
};

function applyOptionalColumns(
  relationship: string | undefined,
  t: Entity,
  row: Row,
): void {
  const props = relationship ? OPTIONAL_COLUMNS[relationship] : undefined;
  if (!props) return;
  for (const prop of props) {
    const column = prop.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
    const value = t[prop] ?? t[column];
    // `!= null`, not `!== undefined`: an explicit null must be treated as unset
    // too. `record_contacts.is_primary` and `record_attachments.is_primary` are
    // both BOOLEAN DEFAULT FALSE, and setting them to null writes NULL over
    // that default — the same defect that made a time row's NOT NULL timezone
    // reject the whole insert.
    //
    // Safe for the nullable-no-default columns in this list (label, position,
    // data, context) because these rows only ever feed INSERTs: HANDLERS holds
    // insert/delete pairs and no update, so on an insert "absent" and
    // "explicit null" both yield NULL and only a defaulted column can tell
    // them apart. There is no clear-a-value semantics here to preserve.
    if (value != null) row[column] = value;
  }
}

export interface RelationOp {
  field: AugerField;
  add: Entity[];
  removeRowIds: number[];
  /** Contacts / times rows changed in place. Absent or empty for the rest. */
  update?: RowUpdate[];
}

type UpdateManyDoc = TypedDocumentNode<
  unknown,
  { updates: Array<{ where: { id: { _eq: number } }; _set: Row }> }
>;

/** The bulk in-place update for the relations whose rows are their own value.
 *  Junctions have none: changing a junction's target is a different row. */
const UPDATE_MANY: Partial<Record<Relation, UpdateManyDoc>> = {
  contacts: updateManyRecordContacts as UpdateManyDoc,
  times: updateManyRecordTimes as UpdateManyDoc,
};

/** What each relation's bulk insert returns. */
interface InsertResults {
  links: ResultOf<typeof insertManyRecordLinks>;
  times: ResultOf<typeof createManyRecordTimes>;
  contacts: ResultOf<typeof addManyRecordContacts>;
  taggings: ResultOf<typeof insertManyRecordTaggings>;
  attachments: ResultOf<typeof insertManyRecordAttachments>;
  sources: ResultOf<typeof insertManyRecordSources>;
}

/** The six owned-row relations a write can insert into. */
export type Relation = keyof InsertResults;

type DeleteDoc = TypedDocumentNode<unknown, { ids: number[] }>;

/** One relation's write handler. `insert` keeps its codegen result type, so
 *  `returned` reads the inserted rows with no cast. */
type Handler<R extends Relation> = {
  insert: TypedDocumentNode<InsertResults[R], { objects: Row[] }>;
  delete: DeleteDoc;
  returned: (res: InsertResults[R]) => WrittenRowTypes[R][];
};

/**
 * Widen an insert document's variables to the rows `buildObjects` builds.
 *
 * Cast kept: `buildObjects` builds each relation's rows at run time from the
 * field type and the targets (`Row`), which the per-table insert input types
 * cannot check. Only the variables are widened; the result type is kept, so
 * the rows a write returns stay typed.
 */
const rowsInsert = <D>(
  doc: TypedDocumentNode<D, never>,
): TypedDocumentNode<D, { objects: Row[] }> =>
  doc as TypedDocumentNode<D, { objects: Row[] }>;
const idsDelete = (doc: TypedDocumentNode<unknown, never>): DeleteDoc =>
  doc as DeleteDoc;

/** The bulk insert/delete pair for each owned-row relation. All six of them. */
const HANDLERS: { [R in Relation]: Handler<R> } = {
  links: {
    insert: rowsInsert(insertManyRecordLinks),
    delete: idsDelete(deleteLinksByIds),
    returned: (res) => res.insert_record_links?.returning ?? [],
  },
  times: {
    insert: rowsInsert(createManyRecordTimes),
    delete: idsDelete(deleteManyRecordTimes),
    returned: (res) => res.insert_record_times?.returning ?? [],
  },
  contacts: {
    insert: rowsInsert(addManyRecordContacts),
    delete: idsDelete(deleteManyRecordContacts),
    returned: (res) => res.insert_record_contacts?.returning ?? [],
  },
  taggings: {
    insert: rowsInsert(insertManyRecordTaggings),
    delete: idsDelete(deleteTaggingsByIds),
    returned: (res) => res.insert_record_taggings?.returning ?? [],
  },
  attachments: {
    insert: rowsInsert(insertManyRecordAttachments),
    delete: idsDelete(deleteAttachmentsByIds),
    returned: (res) => res.insert_record_attachments?.returning ?? [],
  },
  sources: {
    insert: rowsInsert(insertManyRecordSources),
    delete: idsDelete(deleteSourcesByIds),
    returned: (res) => res.insert_record_sources?.returning ?? [],
  },
};

/** Whether `name` is one of the six owned-row relations. */
export const isRelation = (name: string | undefined): name is Relation =>
  name != null && Object.hasOwn(HANDLERS, name);

/** The owned-row relation a field writes. Throws for a field that has none. */
export function relationOf(field: AugerField): Relation {
  const def = getFieldType(field.type);
  const relationship = isReferenceDef(def) ? def.relationship : undefined;
  if (!isRelation(relationship)) {
    throw new Error(
      `applyRelationOps: no write handler for "${field.key}" (${relationship})`,
    );
  }
  return relationship;
}

/** Target entities → insert rows.
 *
 *  Two shapes. Junctions — links, taggings, attachments — point at something else
 *  and store its id in the FK column the field type declares (`reference.column`:
 *  `linked_id`, `tag_id`, `asset_id`). Value rows — contacts, times, sources —
 *  have no target: the row IS the value, so its columns come from the entity.
 *
 *  Attachments are a junction that also needs a derived column; see below. */
function buildObjects(
  field: AugerField,
  recordId: number | null,
  targets: Entity[],
): Row[] {
  const def = getFieldType(field.type);
  const relationship = isReferenceDef(def) ? def.relationship : undefined;
  const schema_key = field.schema_key ?? null;
  const schema_id = field.schema_id ?? null;

  // Omitted entirely on a nested insert: Hasura supplies the FK from the parent.
  const parent = recordId == null ? {} : { record_id: recordId };

  if (relationship === "contacts") {
    return targets.map((t) => {
      const contact_type = t.contactType ?? t.contact_type;
      // record_contacts.contact_type is NOT NULL, and nothing derives it from the
      // field type — the target carries it, and the UI supplies it. No default is
      // invented here: a guessed type is a wrong row nobody notices. But an
      // omission has to name the field: Postgres would name only the column.
      // Fail-closed on purpose — unlike the schema_id/schema_key stamp (which
      // is nullable, so a mismatched stamp can safely fall open to an
      // unqualified row), contact_type is NOT NULL with no meaningful default:
      // there is no "untyped contact" state to land in. Throwing here just
      // beats Postgres to the same rejection with a better message.
      if (contact_type == null) {
        throw new Error(
          `buildObjects: "${field.key}" needs contactType on each target ` +
            `(record_contacts.contact_type is NOT NULL and is not derived from the ` +
            `field type)`,
        );
      }
      // `socials` is an AGGREGATE relation — enrichField gives it schema_key null on
      // purpose, and the read path identifies its rows by `label` naming a platform.
      // A social row without a label is unreachable by the reader that wants it.
      if (field.type === "socials" && t.label == null) {
        throw new Error(
          `buildObjects: "${field.key}" needs a label naming the platform on each ` +
            `target — socials rows are unqualified (schema_key null) and the read ` +
            `path finds them by label`,
        );
      }
      const row: Row = {
        ...parent,
        contact_type,
        value: t.value,
        schema_key,
        schema_id,
      };
      applyOptionalColumns("contacts", t, row);
      return row;
    });
  }

  if (relationship === "times") {
    return targets.map((t) => {
      // Same fail-closed reasoning as contact_type and source_type below:
      // record_times.time_type and start_at are both NOT NULL with no default
      // and are the row's identity, so there is no unqualified state to fall
      // open to. Failing here names the field; failing in Postgres names only
      // the column.
      if (t.timeType == null || t.startAt == null) {
        throw new Error(
          `buildObjects: "${field.key}" needs timeType and startAt (got ` +
            `${JSON.stringify({ time_type: t.timeType, start_at: t.startAt })})`,
        );
      }
      assertTimeValues(
        { start_at: t.startAt, end_at: t.endAt, capacity: t.capacity },
        `buildObjects: "${field.key}"`,
      );
      return {
        ...parent,
        time_type: t.timeType,
        title: t.title ?? null,
        description: t.description ?? null,
        start_at: t.startAt,
        end_at: t.endAt ?? null,
        // undefined, not null, for the two columns that HAVE a database default.
        // These rows go out as one `$objects` variable, so an undefined property
        // is dropped by JSON.stringify and the column is genuinely absent from
        // the insert — which is the only way a default applies. An explicit null
        // overwrites it, and for `timezone` that is not a silent drift but a hard
        // failure: record_times.timezone is NOT NULL DEFAULT
        // 'America/Los_Angeles', so Hasura rejects the whole insert with
        // "unexpected null value for type 'String'". all_day is nullable, so a
        // null there merely stores NULL where the column means FALSE.
        //
        // Every other column here is nullable with no default, so null and absent
        // agree and the `?? null` spelling stays.
        timezone: t.timezone ?? undefined,
        all_day: t.allDay ?? undefined,
        rrule: t.rrule ?? null,
        capacity: t.capacity ?? null,
        context: t.context ?? null,
        schema_key,
        schema_id,
      };
    });
  }

  if (relationship === "sources") {
    return targets.map((t) => {
      const source_type = t.sourceType ?? t.source_type;
      const source_id = t.sourceId ?? t.source_id;
      // Both columns are NOT NULL, and the naming is easy to get wrong from the
      // outside. Failing here names the field; failing in Postgres names only the
      // column.
      // Same fail-closed reasoning as contact_type above: source_type/source_id
      // are the row's identity (NOT NULL), with no unqualified state to fall
      // open to.
      if (source_type == null || source_id == null) {
        throw new Error(
          `buildObjects: "${field.key}" needs sourceType and sourceId (got ` +
            `${JSON.stringify({ source_type, source_id })})`,
        );
      }
      const row: Row = {
        ...parent,
        source_type,
        source_id: String(source_id),
        label: t.label ?? null,
        context: t.context ?? null,
        schema_key,
        schema_id,
      };
      // is_primary is BOOLEAN DEFAULT FALSE, so it has to come through the
      // omit-when-unset path rather than sit beside the two `?? null` columns
      // above — a null there would store NULL over the default.
      applyOptionalColumns("sources", t, row);
      return row;
    });
  }

  const column = (isReferenceDef(def) && def.reference?.column) || "";
  return targets.map((t) => {
    const row: Row = {
      ...parent,
      [column]: Number(t.id),
      schema_key,
      schema_id,
    };
    // record_attachments.asset_type is NOT NULL with no default, and describes the
    // ASSET rather than the field. Derived from the asset's mime_type, which
    // records.create/update look up for a target given by bare id
    // (`resolveAssetTargets`). `assetType` (applied by applyOptionalColumns below)
    // overrides it when the caller has already classified the file.
    if (relationship === "attachments") {
      assertAccepts(field, t);
      row.asset_type = inferAssetType(t.mime_type);
    }
    applyOptionalColumns(relationship, t, row);
    return row;
  });
}

/** A subtype that holds one kind of file (`accepts`) refuses any other. The
 *  target must carry its mime_type — an id alone can't say what the file is. */
function assertAccepts(field: AugerField, t: Entity): void {
  const def = getFieldType(field.type);
  const accepts = isReferenceDef(def) ? def.accepts : undefined;
  if (!accepts) return;
  if (!("mime_type" in t)) {
    throw new Error(
      `buildObjects: "${field.key}" takes ${accepts} files, and asset #${t.id} ` +
        `came without its mime_type — pass the asset, or write through ` +
        `records.create/update, which look it up`,
    );
  }
  const view = assetView(t.mime_type);
  if (view !== accepts) {
    const name = typeof t.name === "string" ? `"${t.name}"` : `asset #${t.id}`;
    throw new Error(
      `"${field.label || field.key}" takes ${accepts} files; ${name} is ${A_FILE[view]}`,
    );
  }
}
const A_FILE: Record<AssetView, string> = {
  image: "an image",
  audio: "an audio file",
  video: "a video",
  document: "a document",
};

/** Look up the mime_type (and name) of every attachment target given without
 *  one, so `buildObjects` can derive asset_type and check a subtype's `accepts`.
 *  An id that matches no asset refuses the write. Mutates the ops' `add`. */
export async function resolveAssetTargets(query: Query, ops: RelationOp[]): Promise<void> {
  const bare = ops.flatMap(({ field, add }) =>
    relationOf(field) === "attachments" ? add.filter((t) => !("mime_type" in t)) : [],
  );
  if (!bare.length) return;
  const ids = [...new Set(bare.map((t) => Number(t.id)))];
  const { assets } = await query(fetchAssetsWhere, { where: { id: { _in: ids } } });
  const byId = new Map(assets.map((a) => [a.id, a]));
  for (const t of bare) {
    const asset = byId.get(Number(t.id));
    if (!asset) throw new Error(`Asset #${t.id} not found`);
    t.mime_type = asset.mime_type;
    t.name ??= asset.name;
  }
}

/**
 * How many rows a mutation actually touched: `affected_rows` on a delete,
 * `returning.length` on an insert. Returns null when the shape says neither, so a
 * caller-supplied `mutate` that returns something else is not treated as zero.
 */
function rowsTouched(res: unknown): number | null {
  const root = Object.values((res ?? {}) as Record<string, unknown>)[0] as
    { affected_rows?: unknown; returning?: unknown } | undefined;
  if (!root || typeof root !== "object") return null;
  if (typeof root.affected_rows === "number") return root.affected_rows;
  if (Array.isArray(root.returning)) return root.returning.length;
  return null;
}

/** The row type each relation's write reports: its insert's `returning`
 *  fragment. A row the record already held is read from the record, whose
 *  selection includes the same fragment. */
export interface WrittenRowTypes {
  links: RecordLinkFieldsFragment;
  times: RecordTimeFieldsFragment;
  contacts: RecordContactFieldsFragment;
  taggings: RecordTaggingFieldsFragment;
  attachments: RecordAttachmentFieldsFragment;
  sources: RecordSourceFieldsFragment;
}

/**
 * A row for each relation target one write added, by relation, then by field
 * key: `written.contacts.email`, `written.links.related_to`. Adding something
 * that already exists returns the thing that already exists, so this holds the
 * rows the write inserted or updated in place, and then (filled in by `records.update`) the field's
 * current row for each `add` target it already held. Removals don't appear: a
 * removal is addressed by a target the caller already held.
 *
 * Keyed by relation first so each row is typed: the caller names the relation
 * of the field it wrote, and reads that relation's fragment with no cast. The
 * write's inserts are where each row's type comes from (codegen), not a cast.
 */
export type WrittenRows = { [R in Relation]: Record<string, WrittenRowTypes[R][]> };

/** A WrittenRows with nothing written. */
export const noRowsWritten = (): WrittenRows => ({
  links: {},
  times: {},
  contacts: {},
  taggings: {},
  attachments: {},
  sources: {},
});

/** Add `rows` to what `written` holds for `relation`'s field `key`. */
export function addWritten<R extends Relation>(
  written: WrittenRows,
  relation: R,
  key: string,
  rows: readonly WrittenRowTypes[R][],
): void {
  if (!rows.length) return;
  const byKey: Record<string, WrittenRowTypes[R][]> = written[relation];
  byKey[key] = [...(byKey[key] ?? []), ...rows];
}

/** Insert one op's rows, typed by its relation. */
async function insertRows<R extends Relation>(
  mutate: Mutate,
  relation: R,
  objects: Row[],
): Promise<{ rows: WrittenRowTypes[R][]; touched: number | null }> {
  const handler: Handler<R> = HANDLERS[relation];
  const res = await mutate(handler.insert, { objects });
  return { rows: handler.returned(res), touched: rowsTouched(res) };
}

/**
 * Apply reconcile ops: delete removed rows, update changed ones in place, then
 * insert added targets.
 *
 * Two limits a caller has to know, both of which follow from Hasura only wrapping
 * root fields that share ONE document:
 *
 * Not atomic. Delete and insert are separate documents, so a failure between them
 * leaves the field WIPED rather than duplicated — old rows gone, new rows never
 * landed — and later ops in the same batch never run. The counts checked below make
 * that visible; they cannot undo it. Fixing it properly means one document per
 * record write, or moving the write into the database.
 *
 * Counts are checked, so a partial write raises. A permission filter that silently
 * dropped half an insert, or a delete against ids that no longer exist, used to be
 * indistinguishable from complete success.
 */
/** A relation op with its handler resolved and its insert rows built. */
export interface PreparedRelationOp {
  field: AugerField;
  relation: Relation;
  objects: Row[];
  removeRowIds: number[];
  update: RowUpdate[];
}

/**
 * Resolve every op's handler and build every insert row — no I/O. Everything
 * that can refuse a write on its inputs (a missing contact type, a time that
 * ends before it starts) refuses here, so a caller that prepares BEFORE its
 * first write leaves nothing half-written when an input is bad.
 */
export function prepareRelationOps(
  recordId: number,
  ops: RelationOp[],
): PreparedRelationOp[] {
  const prepared: PreparedRelationOp[] = [];
  for (const { field, add, removeRowIds, update = [] } of ops) {
    if (!add.length && !removeRowIds.length && !update.length) continue;
    const relation = relationOf(field);
    if (update.length && !UPDATE_MANY[relation]) {
      throw new Error(
        `prepareRelationOps: "${field.key}" (${relation}) has no in-place update`,
      );
    }
    if (relation === "times") {
      for (const u of update) {
        assertTimeValues(u.changes, `prepareRelationOps: "${field.key}" row ${u.id}`);
      }
    }
    const objects = add.length ? buildObjects(field, recordId, add) : [];
    prepared.push({ field, relation, objects, removeRowIds, update });
  }
  return prepared;
}

export async function applyRelationOps(
  mutate: Mutate,
  prepared: PreparedRelationOp[],
): Promise<WrittenRows> {
  const written = noRowsWritten();
  for (const { field, relation, objects, removeRowIds, update } of prepared) {
    if (removeRowIds.length) {
      const res = await mutate(HANDLERS[relation].delete, {
        ids: removeRowIds.map(Number),
      });
      const n = rowsTouched(res);
      if (n != null && n !== removeRowIds.length) {
        throw new Error(
          `applyRelationOps: deleting "${field.key}" removed ${n} of ` +
            `${removeRowIds.length} rows — the field is now partially written`,
        );
      }
    }

    if (update.length) {
      const res = await mutate(UPDATE_MANY[relation]!, {
        updates: update.map((u) => ({
          where: { id: { _eq: u.id } },
          _set: u.changes,
        })),
      });
      // `update_*_many` returns one result per update; an update that matched
      // no row (deleted since the read, or outside the permission filter)
      // reports zero rows rather than an error.
      const results = (Object.values((res ?? {}) as Record<string, unknown>)[0] ??
        []) as Array<{ affected_rows?: number; returning?: unknown[] } | null>;
      const rows = results.flatMap((r) => (r?.returning ?? []) as WrittenRowTypes[typeof relation][]);
      if (Array.isArray(results) && results.length && rows.length !== update.length) {
        throw new Error(
          `applyRelationOps: updating "${field.key}" changed ${rows.length} of ` +
            `${update.length} rows — a row was deleted since it was read, or is ` +
            `not permitted to write`,
        );
      }
      addWritten(written, relation, field.key, rows);
    }

    if (objects.length) {
      const { rows, touched: n } = await insertRows(mutate, relation, objects);
      if (n != null && n !== objects.length) {
        throw new Error(
          `applyRelationOps: inserting "${field.key}" wrote ${n} of ${objects.length} ` +
            `rows. A shortfall here usually means a target already belongs to a ` +
            `DIFFERENT field on this record — the insert adopts unqualified rows ` +
            `but refuses to steal stamped ones; remove it from the other field ` +
            `first. It can also mean a permission filter hid the written row.`,
        );
      }
      addWritten(written, relation, field.key, rows);
    }
  }
  return written;
}

/**
 * Relation ops → nested-insert payloads keyed by relationship name, for
 * `records.create`. `record_id` is omitted so Hasura wires it from the parent it
 * creates in the same transaction.
 *
 * Only `add` is expressible. A new record has nothing to remove, so a non-empty
 * `removeRowIds` means the plan was built against something other than a new
 * record — a caller bug rather than a case to handle.
 */
export function buildRelationInserts(
  ops: RelationOp[],
): Record<string, { data: Row[] }> {
  const nested: Record<string, { data: Row[] }> = {};
  for (const { field, add, removeRowIds, update } of ops) {
    if (removeRowIds.length || update?.length) {
      throw new Error(
        `buildRelationInserts: "${field.key}" has rows to remove or update; a new record has none`,
      );
    }
    if (!add.length) continue;
    const def = getFieldType(field.type);
    const relationship = isReferenceDef(def) ? def.relationship : undefined;
    if (!relationship) {
      throw new Error(
        `buildRelationInserts: no relationship for "${field.key}"`,
      );
    }
    const rows = buildObjects(field, null, add);
    nested[relationship] = {
      data: [...(nested[relationship]?.data ?? []), ...rows],
    };
  }
  return nested;
}
